import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Textarea } from '@/shared/components/ui/textarea';
import { Badge } from '@/shared/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger } from
'@/shared/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogTitle } from
'@/shared/components/ui/dialog';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Users,
  Star,
  Phone,
  Mail,
  Building2,
  User,
  TrendingUp,
  MoreVertical,
  RefreshCw,
  Printer,
  Map as MapIcon,
  RotateCcw,
  X,
  MessageCircle,
  BookOpen,
  Target,
  Eye,
  Pencil,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  HelpCircle,
  ThumbsUp } from
'lucide-react';

const API_URL = API_BASE || (
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'http://localhost:8001'
    : ''
);

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const statusColors = {
  new: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400',
  contacted: 'bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-400',
  qualified: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400',
  proposal: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400',
  negotiation: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-400',
  won: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400',
  lost: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400'
};

const defaultProfile = {
  name: 'Steve',
  age: '47',
  title: 'Warehouse Operations Director',
  company: '',
  email: '',
  phone: '',
  source: 'Website',
  profileImageUrl: '',
  value: '25000',
  probability: '50',
  who: 'Hands-on operator who wants fewer surprises, faster answers, and clean visibility across every customer touchpoint.',
  quote: 'I need to know what is happening before the customer calls me.',
  communicates: 'Direct, practical, numbers-first. Prefers concise updates with clear next actions.',
  reads: 'Industry briefs, operating dashboards, service alerts, and peer recommendations.',
  extraNotes: '',
  howFindsUs: ['Searches for CRM and warehouse workflow fixes', 'Asks peers for systems that are already working', 'Compares implementation speed and support quality'],
  painPoints: ['Customer history is scattered across tools', 'Follow-ups depend on memory instead of process', 'Pipeline value is hard to trust in real time'],
  wantsToKnow: ['How fast the team can start using it', 'Which records are created automatically', 'How reporting proves CRM activity is improving'],
  doesntWant: ['A long setup project before value appears', 'Manual duplicate data entry', 'A generic CRM that ignores logistics operations'],
  whyBuys: ['It makes the next best action obvious', 'It connects customer data to real operating work', 'It gives managers clean pipeline accountability'],
  linkedCampaignIds: [],
  linkedOpportunityIds: []
};

const PROFILE_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
const PROFILE_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const MANAGED_PROFILE_IMAGE_PREFIX = '/uploads/crm/persona-profile-images/';
const profileImageSrc = (url) => url?.startsWith('/uploads/') ? `${API_URL}${url}` : url;

const cloneDefaultProfile = () => ({
  ...defaultProfile,
  howFindsUs: [...defaultProfile.howFindsUs],
  painPoints: [...defaultProfile.painPoints],
  wantsToKnow: [...defaultProfile.wantsToKnow],
  doesntWant: [...defaultProfile.doesntWant],
  whyBuys: [...defaultProfile.whyBuys],
  linkedCampaignIds: [],
  linkedOpportunityIds: []
});

const blankProfile = () => ({
  ...cloneDefaultProfile(),
  name: '',
  age: '',
  title: '',
  company: '',
  email: '',
  phone: '',
  source: '',
  profileImageUrl: '',
  value: '0',
  probability: '50',
  who: '',
  quote: '',
  communicates: '',
  reads: '',
  extraNotes: '',
  howFindsUs: [],
  painPoints: [],
  wantsToKnow: [],
  doesntWant: [],
  whyBuys: []
});

const arrayOrEmpty = (value) => Array.isArray(value) ? value : [];

const personaSections = [
  { key: 'howFindsUs', title: 'How he finds us', icon: Target },
  { key: 'painPoints', title: 'Pain points', icon: AlertTriangle },
  { key: 'wantsToKnow', title: 'What he wants to know', icon: CheckCircle2 },
  { key: 'doesntWant', title: "What he doesn't want", icon: X },
  { key: 'whyBuys', title: 'Why he buys from us?', icon: ThumbsUp }
];

const ProfileFieldHelp = ({ content }) => (
  <TooltipProvider delayDuration={120}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-white/35 bg-white/15 text-white transition hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-white/70"
          aria-label={content}
        >
          <HelpCircle className="h-4 w-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[240px] bg-slate-900 text-white">
        <p>{content}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export const Leads = () => {const { t } = useTranslation("crm");
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingLinks, setSavingLinks] = useState(false);
  const [profile, setProfile] = useState(cloneDefaultProfile());
  const [profileMode, setProfileMode] = useState('create');
  const [linkOptions, setLinkOptions] = useState({ campaigns: [], opportunities: [] });
  const [linkSearch, setLinkSearch] = useState({ campaigns: '', opportunities: '' });
  const [activeLead, setActiveLead] = useState(null);
  const [activeLeadLinks, setActiveLeadLinks] = useState({ campaignIds: [], opportunityIds: [] });
  const [openActionLeadId, setOpenActionLeadId] = useState(null);
  const [profileImageBroken, setProfileImageBroken] = useState(false);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreviewUrl, setProfileImagePreviewUrl] = useState('');
  const profileImageInputRef = useRef(null);
  const profileImagePreviewRef = useRef('');

  const fetchLeads = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/crm/leads`;
      if (statusFilter && statusFilter !== 'all') {
        url += `?status=${statusFilter}`;
      }
      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch leads');
      const data = await response.json();
      setLeads(data);
    } catch (err) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, [statusFilter]);

  const updateProfile = (field, value) => {
    setProfile((current) => ({ ...current, [field]: value }));
  };

  const updateProfileList = (field, index, value) => {
    setProfile((current) => ({
      ...current,
      [field]: current[field].map((item, itemIndex) => itemIndex === index ? value : item)
    }));
  };

  const addProfileListItem = (field) => {
    setProfile((current) => ({ ...current, [field]: [...current[field], ''] }));
  };

  const removeProfileListItem = (field, index) => {
    setProfile((current) => ({
      ...current,
      [field]: current[field].filter((_, itemIndex) => itemIndex !== index)
    }));
  };

  const clearProfileImageSelection = () => {
    if (profileImagePreviewRef.current) {
      URL.revokeObjectURL(profileImagePreviewRef.current);
      profileImagePreviewRef.current = '';
    }
    setProfileImageFile(null);
    setProfileImagePreviewUrl('');
    if (profileImageInputRef.current) profileImageInputRef.current.value = '';
  };

  useEffect(() => () => {
    if (profileImagePreviewRef.current) URL.revokeObjectURL(profileImagePreviewRef.current);
  }, []);

  const resetProfile = () => {
    clearProfileImageSelection();
    setProfile(cloneDefaultProfile());
  };

  const profileFromLeadDetail = (leadDetail) => {
    const persona = leadDetail.persona_profile || {};
    const links = leadDetail.links || [];
    return {
      ...blankProfile(),
      name: leadDetail.contact_name || leadDetail.company || leadDetail.title || '',
      age: persona.age || '',
      title: leadDetail.job_title || '',
      company: leadDetail.company || '',
      email: leadDetail.email || '',
      phone: leadDetail.phone || '',
      source: leadDetail.source || '',
      profileImageUrl: persona.profileImageUrl || leadDetail.profile_image_url || '',
      value: String(leadDetail.value ?? 0),
      probability: String(leadDetail.probability ?? 50),
      who: persona.who || '',
      quote: persona.quote || '',
      communicates: persona.communicates || '',
      reads: persona.reads || '',
      extraNotes: persona.extraNotes || '',
      howFindsUs: arrayOrEmpty(persona.howFindsUs),
      painPoints: arrayOrEmpty(persona.painPoints),
      wantsToKnow: arrayOrEmpty(persona.wantsToKnow),
      doesntWant: arrayOrEmpty(persona.doesntWant),
      whyBuys: arrayOrEmpty(persona.whyBuys),
      linkedCampaignIds: links.filter((link) => link.target_type === 'campaign').map((link) => link.target_id),
      linkedOpportunityIds: links.filter((link) => link.target_type === 'opportunity').map((link) => link.target_id)
    };
  };

  const openCreateProfile = () => {
    setProfileMode('create');
    setActiveLead(null);
    setOpenActionLeadId(null);
    resetProfile();
    setIsProfileModalOpen(true);
  };

  const openLeadProfile = async (lead, mode) => {
    clearProfileImageSelection();
    setProfileMode(mode);
    setActiveLead(lead);
    setOpenActionLeadId(null);
    try {
      const response = await fetch(`${API_URL}/api/crm/leads/${lead.id}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch lead detail');
      const data = await response.json();
      setProfile(profileFromLeadDetail(data));
      setIsProfileModalOpen(true);
    } catch (err) {
      toast.error('Failed to load lead profile');
    }
  };

  const fetchLinkOptions = async (search = '') => {
    try {
      const query = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await fetch(`${API_URL}/api/crm/link-options${query}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch link options');
      const data = await response.json();
      setLinkOptions({
        campaigns: data.campaigns || [],
        opportunities: data.opportunities || []
      });
    } catch (err) {
      toast.error('Failed to load CRM link options');
    }
  };

  useEffect(() => {
    if (isProfileModalOpen || isLinkModalOpen) {
      fetchLinkOptions();
    }
  }, [isProfileModalOpen, isLinkModalOpen]);

  useEffect(() => {
    setProfileImageBroken(false);
  }, [profile.profileImageUrl, profileImagePreviewUrl, isProfileModalOpen]);

  const handleProfileImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!PROFILE_IMAGE_TYPES.has(file.type)) {
      event.target.value = '';
      toast.error('Profile image must be JPEG, PNG, or WebP');
      return;
    }
    if (file.size > PROFILE_IMAGE_MAX_BYTES) {
      event.target.value = '';
      toast.error('Profile image must be 5 MB or smaller');
      return;
    }
    clearProfileImageSelection();
    const previewUrl = URL.createObjectURL(file);
    profileImagePreviewRef.current = previewUrl;
    setProfileImageFile(file);
    setProfileImagePreviewUrl(previewUrl);
  };

  const openProfileImagePicker = () => {
    if (isProfileReadOnly || savingProfile) return;
    if (profileImageInputRef.current) profileImageInputRef.current.value = '';
    profileImageInputRef.current?.click();
  };

  const handleProfileModalOpenChange = (open) => {
    if (!open) clearProfileImageSelection();
    setIsProfileModalOpen(open);
  };

  const toggleProfileLink = (field, id) => {
    setProfile((current) => {
      const selected = new Set(current[field] || []);
      if (selected.has(id)) {
        selected.delete(id);
      } else {
        selected.add(id);
      }
      return { ...current, [field]: Array.from(selected) };
    });
  };

  const toggleLeadLink = (field, id) => {
    setActiveLeadLinks((current) => {
      const selected = new Set(current[field] || []);
      if (selected.has(id)) {
        selected.delete(id);
      } else {
        selected.add(id);
      }
      return { ...current, [field]: Array.from(selected) };
    });
  };

  const buildRecordLinks = (campaignIds, opportunityIds) => [
    ...(campaignIds || []).map((id) => ({
      target_type: 'campaign',
      target_id: Number(id),
      relationship_type: 'belongs_to_campaign'
    })),
    ...(opportunityIds || []).map((id) => ({
      target_type: 'opportunity',
      target_id: Number(id),
      relationship_type: 'related_opportunity'
    }))
  ];

  const openLeadLinkModal = async (lead) => {
    setActiveLead(lead);
    setActiveLeadLinks({ campaignIds: [], opportunityIds: [] });
    setIsLinkModalOpen(true);
    try {
      const response = await fetch(`${API_URL}/api/crm/leads/${lead.id}/links`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch lead links');
      const data = await response.json();
      setActiveLeadLinks({
        campaignIds: (data.links || []).filter((link) => link.target_type === 'campaign').map((link) => link.target_id),
        opportunityIds: (data.links || []).filter((link) => link.target_type === 'opportunity').map((link) => link.target_id)
      });
    } catch (err) {
      toast.error('Failed to load lead links');
    }
  };

  const saveLeadLinks = async () => {
    if (!activeLead) return;
    setSavingLinks(true);
    try {
      const response = await fetch(`${API_URL}/api/crm/leads/${activeLead.id}/links`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          links: buildRecordLinks(activeLeadLinks.campaignIds, activeLeadLinks.opportunityIds)
        })
      });
      if (!response.ok) throw new Error('Failed to save lead links');
      toast.success('Lead links updated');
      setIsLinkModalOpen(false);
      await fetchLeads();
    } catch (err) {
      toast.error('Failed to save lead links');
    } finally {
      setSavingLinks(false);
    }
  };

  const buildProfilePayload = (profileImageUrl = profile.profileImageUrl) => ({
    title: profileMode === 'edit' && activeLead?.title ? activeLead.title : `${profile.name.trim()} - ${profile.company.trim()}`,
    company: profile.company.trim(),
    value: Number(profile.value) || 0,
    source: profile.source || 'Website',
    contact_name: profile.name.trim(),
    email: profile.email.trim() || null,
    phone: profile.phone.trim() || null,
    job_title: profile.title.trim() || null,
    probability: Number(profile.probability) || 50,
    status: activeLead?.status || 'new',
    links: buildRecordLinks(profile.linkedCampaignIds, profile.linkedOpportunityIds),
    persona_profile: {
      profileImageUrl,
      age: profile.age,
      who: profile.who,
      quote: profile.quote,
      communicates: profile.communicates,
      reads: profile.reads,
      extraNotes: profile.extraNotes,
      howFindsUs: profile.howFindsUs,
      painPoints: profile.painPoints,
      wantsToKnow: profile.wantsToKnow,
      doesntWant: profile.doesntWant,
      whyBuys: profile.whyBuys
    }
  });

  const uploadProfileImage = async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    const token = localStorage.getItem('ezerpToken');
    const response = await fetch(`${API_URL}/api/crm/persona-profile/image`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.profile_image_url) {
      throw new Error(data.detail || 'Failed to upload profile image');
    }
    return data.profile_image_url;
  };

  const deleteManagedProfileImage = async (profileImageUrl) => {
    if (!profileImageUrl?.startsWith(MANAGED_PROFILE_IMAGE_PREFIX)) return false;
    const response = await fetch(`${API_URL}/api/crm/persona-profile/image?profile_image_url=${encodeURIComponent(profileImageUrl)}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return response.ok;
  };

  const handleSaveProfile = async () => {
    if (profileMode === 'view') {
      setIsProfileModalOpen(false);
      return;
    }
    if (!profile.name.trim()) {
      toast.error('Profile name is required');
      return;
    }
    if (!profile.company.trim()) {
      toast.error('Company is required');
      return;
    }

    setSavingProfile(true);
    let uploadedProfileImageUrl = '';
    let profileSaved = false;
    try {
      const isEditing = profileMode === 'edit' && activeLead?.id;
      const previousProfileImageUrl = profile.profileImageUrl;
      if (profileImageFile) uploadedProfileImageUrl = await uploadProfileImage(profileImageFile);
      const nextProfileImageUrl = uploadedProfileImageUrl || previousProfileImageUrl;
      const response = await fetch(`${API_URL}/api/crm/leads${isEditing ? `/${activeLead.id}` : ''}`, {
        method: isEditing ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(buildProfilePayload(nextProfileImageUrl))
      });
      const responseData = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(responseData.detail || 'Failed to save profile');
      profileSaved = true;
      if (uploadedProfileImageUrl && previousProfileImageUrl !== uploadedProfileImageUrl) {
        await deleteManagedProfileImage(previousProfileImageUrl).catch(() => false);
      }
      toast.success(isEditing ? 'CRM profile updated' : 'CRM profile created');
      setIsProfileModalOpen(false);
      resetProfile();
      setActiveLead(null);
      await fetchLeads();
    } catch (err) {
      if (uploadedProfileImageUrl && !profileSaved) {
        await deleteManagedProfileImage(uploadedProfileImageUrl).catch(() => false);
      }
      toast.error(err.message || 'Failed to save CRM profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const deleteLead = async (lead) => {
    setOpenActionLeadId(null);
    const confirmed = window.confirm(`Delete ${lead.title}? This cannot be undone.`);
    if (!confirmed) return;
    try {
      const response = await fetch(`${API_URL}/api/crm/leads/${lead.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to delete lead');
      toast.success('Lead deleted');
      await fetchLeads();
    } catch (err) {
      toast.error('Failed to delete lead');
    }
  };

  const filteredLeads = leads.filter((lead) => {
    const matchesSearch = !searchTerm ||
    lead.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.contact_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const stats = {
    total: leads.length,
    new: leads.filter((l) => l.status === 'new').length,
    qualified: leads.filter((l) => l.status === 'qualified').length,
    totalValue: leads.reduce((sum, l) => sum + (l.value || 0), 0)
  };
  const isProfileReadOnly = profileMode === 'view';
  const profileActionLabel = profileMode === 'edit' ? 'Save Changes' : 'Save Profile';
  const isBrazilianRewardsCampaignOption = (option = {}) => {
    const identity = [
      option.label,
      option.name,
      option.title,
      option.description,
      option.slug,
      option.code,
      option.key
    ].filter(Boolean).join(' ').toLowerCase();
    return identity.includes('brazilians rewards') || identity.includes('brazilian rewards');
  };
  const selectedCampaignIds = new Set((profile.linkedCampaignIds || []).map((id) => String(id)));
  const linkedBrazilianRewardsCampaign = (linkOptions.campaigns || []).find((campaign) =>
    selectedCampaignIds.has(String(campaign.id)) && isBrazilianRewardsCampaignOption(campaign)
  );
  const openLinkedBrazilianRewardsMap = () => {
    if (!linkedBrazilianRewardsCampaign?.id) {
      toast.error('This profile is not linked to the Brazilian Rewards campaign');
      return;
    }
    setIsProfileModalOpen(false);
    navigate(`/crm/campaign-intelligence?campaign_id=${encodeURIComponent(linkedBrazilianRewardsCampaign.id)}&tab=map`);
  };

  const renderRecordSelector = ({
    title,
    type,
    options,
    selectedIds,
    onToggle,
    searchValue,
    onSearchChange,
    disabled = false
  }) => {
    const selected = new Set(selectedIds || []);
    const normalizedSearch = searchValue.trim().toLowerCase();
    const visibleOptions = options.filter((option) =>
      !normalizedSearch ||
      option.label?.toLowerCase().includes(normalizedSearch) ||
      option.status?.toLowerCase().includes(normalizedSearch) ||
      option.stage?.toLowerCase().includes(normalizedSearch)
    );

    return (
      <section className="rounded-md border border-slate-200 bg-white p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h4 className="text-sm font-bold text-slate-900">{title}</h4>
            <p className="text-xs text-slate-500">{selected.size} selected</p>
          </div>
          <Badge variant="outline" className="rounded-md">{visibleOptions.length}</Badge>
        </div>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={`Search ${title.toLowerCase()}...`}
            className="h-9 pl-9"
            disabled={disabled}
          />
        </div>
        <div className="max-h-48 overflow-y-auto rounded-md border border-slate-100">
          {visibleOptions.map((option) => (
            <button
              key={`${type}-${option.id}`}
              type="button"
              onClick={() => onToggle(option.id)}
              disabled={disabled}
              className={`flex w-full items-center justify-between gap-3 border-b border-slate-100 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-slate-50 ${
                selected.has(option.id) ? 'bg-teal-50 text-teal-900' : 'bg-white text-slate-800'
              } ${disabled ? 'cursor-default hover:bg-white' : ''}`}
            >
              <span className="min-w-0">
                <span className="block truncate font-medium">{option.label}</span>
                <span className="block truncate text-xs text-slate-500">
                  {option.status || option.stage || 'Active record'}
                </span>
              </span>
              <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                selected.has(option.id) ? 'border-teal-600 bg-teal-600 text-white' : 'border-slate-300 bg-white'
              }`}>
                {selected.has(option.id) ? <CheckCircle2 className="h-3 w-3" /> : null}
              </span>
            </button>
          ))}
          {visibleOptions.length === 0 && (
            <div className="px-3 py-4 text-sm text-slate-500">No records found</div>
          )}
        </div>
      </section>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="crm-leads">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("leads", "Leads")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("manageAndQualify", "Manage and qualify your sales leads")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchLeads} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button className="gap-2 bg-teal-600 hover:bg-teal-700" onClick={openCreateProfile}>
            <Plus className="h-4 w-4" />{t("addProfile", "Add Profile")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <Card className="card-elevated border-l-4 border-l-teal-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-teal-500/10">
                <Users className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("totalLeads", "Total Leads")}</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Star className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("newLeads", "New Leads")}</p>
                <p className="text-2xl font-bold">{stats.new}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("qualified", "Qualified")}</p>
                <p className="text-2xl font-bold">{stats.qualified}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-orange-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/10">
                <TrendingUp className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("pipelineValue", "Pipeline Value")}</p>
                <p className="text-2xl font-bold">${(stats.totalValue / 1000).toFixed(0)}k</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="card-elevated">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search leads by name, company, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10" />
              
            </div>
            <div className="flex gap-2 flex-wrap">
              {['all', 'new', 'contacted', 'qualified', 'proposal', 'negotiation'].map((status) =>
              <Button
                key={status}
                variant={statusFilter === status ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter(status)}
                className={statusFilter === status ? 'bg-teal-600 hover:bg-teal-700' : ''}>
                
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card className="card-elevated">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("lead", "Lead")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("company", "Company")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("source", "Source")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("probability", "Probability")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("value", "Value")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("status", "Status")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("owner", "Owner")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((lead) =>
                <tr key={lead.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-teal-500/10 flex items-center justify-center">
                          <span className="text-sm font-semibold text-teal-600">
                            {lead.contact_name?.split(' ').map((n) => n[0]).join('') || 'L'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{lead.title}</p>
                          <p className="text-xs text-muted-foreground">{lead.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{lead.company}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <div>{lead.source}</div>
                      {(lead.linked_campaign_count || lead.linked_opportunity_count) ? (
                        <div className="mt-1 flex flex-wrap gap-1 text-[11px] text-muted-foreground">
                          {lead.linked_campaign_count ? <span>{lead.linked_campaign_count} campaign</span> : null}
                          {lead.linked_opportunity_count ? <span>{lead.linked_opportunity_count} opportunity</span> : null}
                        </div>
                      ) : null}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className={`inline-flex items-center justify-center h-8 w-8 rounded-full text-sm font-bold ${
                    lead.probability >= 80 ? 'bg-emerald-100 text-emerald-700' :
                    lead.probability >= 60 ? 'bg-teal-100 text-teal-700' :
                    lead.probability >= 40 ? 'bg-amber-100 text-amber-700' :
                    'bg-gray-100 text-gray-700'}`
                    }>
                        {lead.probability}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right font-semibold">
                      ${lead.value?.toLocaleString() || 0}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <Badge className={statusColors[lead.status] || statusColors.new}>
                        {lead.status}
                      </Badge>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{lead.owner}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Phone className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Mail className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="Link records" onClick={() => openLeadLinkModal(lead)}>
                          <Target className="h-4 w-4" />
                        </Button>
                        <div className="relative">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setOpenActionLeadId(openActionLeadId === lead.id ? null : lead.id)}>
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                        {openActionLeadId === lead.id && (
                          <div className="absolute right-0 top-9 z-20 w-36 rounded-md border border-slate-200 bg-white py-1 text-sm shadow-lg">
                            <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-slate-700 hover:bg-slate-50" onClick={() => openLeadProfile(lead, 'view')}>
                              <Eye className="h-4 w-4" /> View
                            </button>
                            <button type="button" className="flex w-full items-center gap-2 px-3 py-2 text-left text-slate-700 hover:bg-slate-50" onClick={() => openLeadProfile(lead, 'edit')}>
                              <Pencil className="h-4 w-4" /> Edit
                            </button>
                            <button type="button" className="flex w-full items-center gap-2 border-t border-slate-100 px-3 py-2 text-left text-red-600 hover:bg-red-50" onClick={() => deleteLead(lead)}>
                              <Trash2 className="h-4 w-4" /> Delete
                            </button>
                          </div>
                        )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredLeads.length === 0 &&
          <div className="py-12 text-center">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t("noLeadsFound", "No leads found")}</h3>
              <p className="text-muted-foreground">{t("tryAdjustingYour", "Try adjusting your search or filter criteria")}</p>
            </div>
          }
        </CardContent>
      </Card>

      <Dialog open={isProfileModalOpen} onOpenChange={handleProfileModalOpenChange}>
        <DialogContent className="max-w-[1080px] max-h-[92vh] overflow-hidden gap-0 border-0 bg-[#c8d6e0] p-0 shadow-2xl sm:rounded-none md:sm:rounded-lg">
          <DialogTitle className="sr-only">{profileMode === 'view' ? 'View Profile' : profileMode === 'edit' ? 'Edit Profile' : 'Persona Profile'}</DialogTitle>
          <div className="flex max-h-[92vh] flex-col overflow-hidden">
            <div className="bg-[#d93b30] px-6 py-4 text-center">
              <h2 className="font-['Barlow_Condensed'] text-3xl font-black uppercase tracking-[0.18em] text-white">
                Persona Profile
              </h2>
            </div>

            <div className="grid flex-1 overflow-y-auto md:grid-cols-[300px_1fr]">
              <aside className="bg-[#d93b30] p-6 text-white">
                <input
                  ref={profileImageInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="sr-only"
                  tabIndex={-1}
                  aria-hidden="true"
                  onChange={handleProfileImageChange}
                />
                <button
                  type="button"
                  className={`mx-auto mb-5 flex h-32 w-32 items-center justify-center rounded-full border-4 border-white/95 bg-white ${isProfileReadOnly ? 'cursor-default' : 'cursor-pointer focus:outline-none focus:ring-4 focus:ring-white/60'}`}
                  onClick={openProfileImagePicker}
                  disabled={isProfileReadOnly || savingProfile}
                  aria-label={isProfileReadOnly ? 'Profile image' : 'Select or replace profile image'}
                  title={isProfileReadOnly ? 'Profile image' : 'Select or replace profile image'}
                >
                  {(profileImagePreviewUrl || profile.profileImageUrl) && !profileImageBroken ? (
                    <img
                      src={profileImagePreviewUrl || profileImageSrc(profile.profileImageUrl)}
                      alt={`${profile.company || profile.name || 'Profile'} logo`}
                      className="h-24 w-24 rounded-full object-contain"
                      onError={() => setProfileImageBroken(true)}
                    />
                  ) : (
                    <svg viewBox="0 0 120 120" className="h-24 w-24" aria-hidden="true">
                      <circle cx="60" cy="44" r="24" fill="#3d4f5c" />
                      <path d="M25 105c5-25 20-38 35-38s30 13 35 38" fill="#3d4f5c" />
                      <path d="M42 43c2-19 34-23 41-3-13-6-25 1-41 3z" fill="#d93b30" />
                      <circle cx="51" cy="48" r="2.5" fill="#fff" />
                      <circle cx="69" cy="48" r="2.5" fill="#fff" />
                      <path d="M52 57c5 4 11 4 16 0" stroke="#fff" strokeWidth="3" fill="none" strokeLinecap="round" />
                    </svg>
                  )}
                </button>

                <div className="mb-5 grid grid-cols-[1fr_70px] gap-3">
                  <Input value={profile.name} readOnly={isProfileReadOnly} onChange={(e) => updateProfile('name', e.target.value)} className="border-white/30 bg-white/15 text-xl font-black uppercase text-white placeholder:text-white/70" />
                  <Input value={profile.age} readOnly={isProfileReadOnly} onChange={(e) => updateProfile('age', e.target.value)} className="border-white/30 bg-white/15 text-xl font-black text-white placeholder:text-white/70" />
                </div>

                <div className="space-y-3">
                  <div>
                    <p className="mb-1 text-xs font-bold uppercase tracking-widest text-white/80">Role</p>
                    <Input value={profile.title} readOnly={isProfileReadOnly} onChange={(e) => updateProfile('title', e.target.value)} className="border-white/30 bg-white/15 text-white placeholder:text-white/70" />
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-bold uppercase tracking-widest text-white/80">Company</p>
                    <Input value={profile.company} readOnly={isProfileReadOnly} onChange={(e) => updateProfile('company', e.target.value)} placeholder="Company name" className="border-white/30 bg-white/15 text-white placeholder:text-white/70" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      value={profile.value}
                      readOnly={isProfileReadOnly}
                      onChange={(e) => updateProfile('value', e.target.value)}
                      placeholder="Value"
                      aria-label="Estimated value"
                      className="h-10 w-[105px] border-white/30 bg-white/15 text-white placeholder:text-white/70"
                    />
                    <ProfileFieldHelp content="Estimated value of this lead or opportunity. Use 0 when there is no sales value yet." />
                    <Input
                      value={profile.probability}
                      readOnly={isProfileReadOnly}
                      onChange={(e) => updateProfile('probability', e.target.value)}
                      placeholder="Score"
                      aria-label="Probability score"
                      className="h-10 w-[68px] border-white/30 bg-white/15 text-white placeholder:text-white/70"
                    />
                    <ProfileFieldHelp content="Probability or lead score from 0 to 100. It helps rank how likely this profile is to become a real opportunity." />
                  </div>
                </div>

                <div className="mt-6 space-y-5">
                  <section>
                    <h3 className="mb-2 border-b border-white/35 pb-1 text-lg font-black uppercase tracking-wide">Who is he?</h3>
                    <Textarea value={profile.who} readOnly={isProfileReadOnly} onChange={(e) => updateProfile('who', e.target.value)} className="min-h-[110px] border-white/30 bg-white/15 text-white placeholder:text-white/70" />
                  </section>
                  <section>
                    <h3 className="mb-2 border-b border-white/35 pb-1 text-lg font-black uppercase tracking-wide">Quote</h3>
                    <Textarea value={profile.quote} readOnly={isProfileReadOnly} onChange={(e) => updateProfile('quote', e.target.value)} className="min-h-[80px] border-white/30 bg-white/15 text-white placeholder:text-white/70" />
                  </section>
                  <section>
                    <h3 className="mb-2 flex items-center gap-2 border-b border-white/35 pb-1 text-lg font-black uppercase tracking-wide">
                      <MessageCircle className="h-4 w-4" /> How he communicates
                    </h3>
                    <Textarea value={profile.communicates} readOnly={isProfileReadOnly} onChange={(e) => updateProfile('communicates', e.target.value)} className="min-h-[90px] border-white/30 bg-white/15 text-white placeholder:text-white/70" />
                  </section>
                  <section>
                    <h3 className="mb-2 flex items-center gap-2 border-b border-white/35 pb-1 text-lg font-black uppercase tracking-wide">
                      <BookOpen className="h-4 w-4" /> What he reads
                    </h3>
                    <Textarea value={profile.reads} readOnly={isProfileReadOnly} onChange={(e) => updateProfile('reads', e.target.value)} className="min-h-[90px] border-white/30 bg-white/15 text-white placeholder:text-white/70" />
                  </section>
                </div>
              </aside>

              <main className="p-6">
                <div className="mb-5 grid gap-3 sm:grid-cols-3">
                  <Input value={profile.email} readOnly={isProfileReadOnly} onChange={(e) => updateProfile('email', e.target.value)} placeholder="profile@email.com" className="bg-white" />
                  <Input value={profile.phone} readOnly={isProfileReadOnly} onChange={(e) => updateProfile('phone', e.target.value)} placeholder="+1 555 000 0000" className="bg-white" />
                  <Input value={profile.source} readOnly={isProfileReadOnly} onChange={(e) => updateProfile('source', e.target.value)} placeholder="Source" className="bg-white" />
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                  {personaSections.map((section) => {
                    const Icon = section.icon;
                    return (
                      <section key={section.key} className="rounded-md border border-black/10 bg-white p-5 shadow-sm">
                        <div className="mb-4 flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#d93b30] text-white">
                            <Icon className="h-5 w-5" />
                          </div>
                          <h3 className="text-xl font-black uppercase tracking-wide text-[#d93b30]">{section.title}</h3>
                        </div>
                        <div className="space-y-3">
                          {profile[section.key].map((item, index) =>
                            <div key={`${section.key}-${index}`} className="flex items-center gap-2">
                              <span className="h-2 w-2 shrink-0 rounded-full bg-[#d93b30]" />
                              <Input
                                value={item}
                                readOnly={isProfileReadOnly}
                                onChange={(e) => updateProfileList(section.key, index, e.target.value)}
                                className="border-0 border-b border-slate-200 bg-transparent px-1 shadow-none focus-visible:ring-0" />
                              {!isProfileReadOnly && <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-slate-400 hover:text-[#d93b30]" onClick={() => removeProfileListItem(section.key, index)}>
                                <X className="h-4 w-4" />
                              </Button>}
                            </div>
                          )}
                          {!isProfileReadOnly && <Button type="button" variant="outline" size="sm" className="mt-2 gap-2 border-[#d93b30] text-[#d93b30] hover:bg-[#d93b30] hover:text-white" onClick={() => addProfileListItem(section.key)}>
                            <Plus className="h-4 w-4" /> Add item
                          </Button>}
                        </div>
                      </section>
                    );
                  })}
                </div>

                <div className="mt-5 rounded-md border border-black/10 bg-white p-5 shadow-sm">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#d93b30] text-white">
                      <Target className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-xl font-black uppercase tracking-wide text-[#d93b30]">Linked CRM Records</h3>
                    </div>
                  </div>
                  <div className="grid gap-4 lg:grid-cols-2">
                    {renderRecordSelector({
                      title: 'Campaigns',
                      type: 'campaign',
                      options: linkOptions.campaigns,
                      selectedIds: profile.linkedCampaignIds,
                      onToggle: (id) => toggleProfileLink('linkedCampaignIds', id),
                      searchValue: linkSearch.campaigns,
                      onSearchChange: (value) => setLinkSearch((current) => ({ ...current, campaigns: value })),
                      disabled: isProfileReadOnly
                    })}
                    {renderRecordSelector({
                      title: 'Opportunities',
                      type: 'opportunity',
                      options: linkOptions.opportunities,
                      selectedIds: profile.linkedOpportunityIds,
                      onToggle: (id) => toggleProfileLink('linkedOpportunityIds', id),
                      searchValue: linkSearch.opportunities,
                      onSearchChange: (value) => setLinkSearch((current) => ({ ...current, opportunities: value })),
                      disabled: isProfileReadOnly
                    })}
                  </div>
                </div>

                <div className="mt-5 rounded-md border border-black/10 bg-white p-5 shadow-sm">
                  <label htmlFor="persona-extra-notes" className="mb-2 block text-sm font-bold uppercase tracking-[0.16em] text-[#d93b30]">
                    Extra Notes
                  </label>
                  <Textarea
                    id="persona-extra-notes"
                    value={profile.extraNotes}
                    readOnly={isProfileReadOnly}
                    onChange={(e) => updateProfile('extraNotes', e.target.value)}
                    placeholder="Add notes or extra context for this persona profile..."
                    className="min-h-[110px] resize-y bg-white"
                  />
                </div>
              </main>
            </div>

            <div className="flex flex-col gap-3 border-t border-black/10 bg-white px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm font-bold uppercase tracking-[0.16em] text-[#d93b30]">Persona Profile</div>
              <div className="flex flex-wrap gap-2">
                {linkedBrazilianRewardsCampaign && (
                  <Button type="button" variant="outline" className="gap-2" onClick={openLinkedBrazilianRewardsMap}>
                    <MapIcon className="h-4 w-4" /> Map
                  </Button>
                )}
                <Button type="button" variant="outline" className="gap-2" onClick={() => window.print()}>
                  <Printer className="h-4 w-4" /> Print
                </Button>
                {!isProfileReadOnly && <Button type="button" variant="outline" className="gap-2" onClick={resetProfile}>
                  <RotateCcw className="h-4 w-4" /> Reset
                </Button>}
                {isProfileReadOnly ? (
                  <Button type="button" className="bg-[#334155] hover:bg-[#1f2937]" onClick={() => handleProfileModalOpenChange(false)}>
                    Close
                  </Button>
                ) : (
                  <Button type="button" className="gap-2 bg-[#d93b30] hover:bg-[#b92f26]" onClick={handleSaveProfile} disabled={savingProfile}>
                    <Plus className="h-4 w-4" /> {savingProfile ? 'Saving...' : profileActionLabel}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isLinkModalOpen} onOpenChange={setIsLinkModalOpen}>
        <DialogContent className="max-w-[860px] gap-0 p-0">
          <DialogTitle className="border-b px-6 py-4">
            <div className="text-xl font-semibold">Linked CRM Records</div>
            <div className="mt-1 text-sm font-normal text-muted-foreground">{activeLead?.title}</div>
          </DialogTitle>
          <div className="grid gap-4 p-6 md:grid-cols-2">
            {renderRecordSelector({
              title: 'Campaigns',
              type: 'campaign',
              options: linkOptions.campaigns,
              selectedIds: activeLeadLinks.campaignIds,
              onToggle: (id) => toggleLeadLink('campaignIds', id),
              searchValue: linkSearch.campaigns,
              onSearchChange: (value) => setLinkSearch((current) => ({ ...current, campaigns: value }))
            })}
            {renderRecordSelector({
              title: 'Opportunities',
              type: 'opportunity',
              options: linkOptions.opportunities,
              selectedIds: activeLeadLinks.opportunityIds,
              onToggle: (id) => toggleLeadLink('opportunityIds', id),
              searchValue: linkSearch.opportunities,
              onSearchChange: (value) => setLinkSearch((current) => ({ ...current, opportunities: value }))
            })}
          </div>
          <div className="flex justify-end gap-2 border-t px-6 py-4">
            <Button type="button" variant="outline" onClick={() => setIsLinkModalOpen(false)}>
              Cancel
            </Button>
            <Button type="button" className="bg-teal-600 hover:bg-teal-700" onClick={saveLeadLinks} disabled={savingLinks}>
              {savingLinks ? 'Saving...' : 'Save Links'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>);

};
